import { useTranslation } from 'react-i18next';
import { Check, Clock, Loader2, RotateCcw, TriangleAlert } from 'lucide-react';
import { CustomerOrderStatus, OrderStatus, type Order } from '@/lib/shared-types';
import { toGuestLines, type GuestLine } from '@/lib/domain';
import { formatDateTime } from '@/lib/i18n';
import { orderStatusColors } from '@/lib/ui-tokens';
import { readableInk } from '@/lib/contrast';
import { isRunningLate } from '@/lib/guest';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

type TrackedOrder = Order & { scheduledFor?: string | null };

interface Props {
  orders: TrackedOrder[];
  showStationName: boolean;
  showDelayUpdates: boolean;
  showEta: boolean;
  /** Station names by item id, only fetched when the venue shows them. */
  stations: Record<string, string | undefined>;
  now: number;
  onReorder: (order: TrackedOrder) => void;
}

/** Statuses whose line is finished, so it stops competing for attention. */
const SETTLED: CustomerOrderStatus[] = [CustomerOrderStatus.PICKED_UP, CustomerOrderStatus.SERVED];

/**
 * The guest's food: one card per order, each card a list of its lines.
 *
 * Not one tall card per order with a five-step progress bar, which answered
 * "where is my coffee?" with the kitchen's bookkeeping; and not a single flat
 * column, which wasted every pixel past ~400 and left a tablet one narrow
 * ribbon of text down the middle of the screen.
 *
 * So: the LINE is still the unit of information — its own state, never merged
 * across orders — and the ORDER is the unit of layout, because it is what a
 * guest names out loud to a waiter and what re-ordering hangs off. Cards flow
 * into as many columns as the viewport can hold.
 */
export function GuestLineList({
  orders,
  showStationName,
  showDelayUpdates,
  showEta,
  stations,
  now,
  onReorder,
}: Props) {
  const { t, i18n } = useTranslation();

  // Group the flattened lines back by their order. `toGuestLines` has already
  // put the newest order first and preserved item order inside each, so
  // appending in sequence keeps both without a second sort. The cards below
  // flow in plain grid order — no `grid-flow-dense`, no `order-*` — so the
  // newest order lands in the first cell at every breakpoint, and in the
  // top-start corner in Arabic as well as English.
  const groups: { order: TrackedOrder; lines: GuestLine<Order['items'][number], TrackedOrder>[] }[] =
    [];
  for (const line of toGuestLines(orders)) {
    const last = groups[groups.length - 1];
    if (last && last.order.id === line.order.id) last.lines.push(line);
    else groups.push({ order: line.order, lines: [line] });
  }

  return (
    /* One column on a phone, more as the viewport allows. `items-start` so a
       short card does not stretch to match a tall neighbour in the same row. */
    <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {groups.map(({ order, lines }) => {
        const cancelledOrder =
          order.status === OrderStatus.CANCELLED || order.status === OrderStatus.VOIDED;
        const late = showDelayUpdates && isRunningLate(order, now);

        return (
          <Card key={order.id} className="min-w-0">
            <CardContent className="space-y-3 p-3">
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium tabular-nums">#{order.number}</span>
                  {showEta && order.scheduledFor && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3 shrink-0" />
                      {t('guest.track.readyAt', {
                        time: formatDateTime(order.scheduledFor, i18n.language),
                      })}
                    </span>
                  )}
                  {late && (
                    <span className="flex items-center gap-1 text-notice">
                      <TriangleAlert className="h-3 w-3 shrink-0" />
                      {t('guest.track.delayed')}
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 gap-1 px-2 text-xs"
                  onClick={() => onReorder(order)}
                >
                  <RotateCcw className="h-3 w-3" />
                  {t('guest.track.reorder')}
                </Button>
              </div>

              <ol className="space-y-1">
                {lines.map(({ item, status }) => {
                  // A cancelled line is struck through, not removed: a row that
                  // vanishes reads as the app having lost it.
                  const gone = status === null || cancelledOrder;
                  const settled = status !== null && SETTLED.includes(status);

                  return (
                    <li
                      key={item.id}
                      className="flex min-w-0 flex-wrap items-start justify-between gap-x-3 gap-y-1 border-t pt-2 first:border-t-0 first:pt-0"
                    >
                      <span
                        className={`min-w-0 break-words text-sm ${gone ? 'text-muted-foreground line-through' : ''}`}
                      >
                        <span className="tabular-nums">{Math.round(Number(item.quantity))}</span> ×{' '}
                        {item.name}
                        {showStationName && stations[item.id] && (
                          <span className="ms-1 text-xs text-muted-foreground">
                            · {stations[item.id]}
                          </span>
                        )}
                      </span>

                      {gone ? (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {t('guest.track.itemRemoved')}
                        </span>
                      ) : (
                        <span className="flex shrink-0 items-center gap-1.5">
                          {/* The icon repeats what the pill says, for anyone who
                              cannot separate the status colours. */}
                          {settled ? (
                            <Check className="h-3.5 w-3.5 text-positive" />
                          ) : (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
                          )}
                          <span
                            className="rounded-full px-2 py-0.5 text-xs font-medium"
                            style={{
                              background: orderStatusColors[status],
                              color: readableInk(orderStatusColors[status]),
                            }}
                          >
                            {t(`order.status.${status}`)}
                          </span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
